import { STORAGE_KEYS, type IDatabaseService, type IMetadataKeyRepository } from '@app/domain/ports';
import { initializeStoragePersistence } from './services/storage.service';
import {
  assertExistingOpfsDatabaseFile,
  collectOpfsDatabaseDiagnostics,
  logOpfsDatabaseDiagnostics,
} from './utils/opfs-database.utils';

type SqliteMode = 'straight' | 'worker' | 'opfs' | 'native';

const getNowMs = (): number => {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }

  return Date.now();
};

/**
 * Storage Lifecycle Manager
 * Handles all storage infrastructure initialization concerns
 * Encapsulates storage persistence, migrations, and setup logic
 */
export class StorageLifecycleManager {
  private storageInitializationPromise: Promise<void> | null = null;
  private opfsInitializationPromise: Promise<void> | null = null;

  constructor(
    private readonly sqliteMode: SqliteMode,
    private readonly databaseService: Pick<IDatabaseService, 'initialize' | 'isInitialized'>,
    private readonly metadataRepository: Pick<IMetadataKeyRepository, 'get'>,
    private readonly isNativePlatform = false,
  ) {}

  /**
   * Initialize all storage infrastructure concerns
   * Called once during app bootstrap
   */
  async initialize(): Promise<void> {
    await this.initializeStoragePersistenceOnce();
    await this.initializeStartupDatabaseOnce();
  }

  private async initializeStartupDatabaseOnce(): Promise<void> {
    const startedAt = getNowMs();

    if (this.sqliteMode !== 'opfs') {
      this.logStartupMetric('skipped_non_opfs', startedAt);
      return;
    }

    if (this.databaseService.isInitialized()) {
      this.logStartupMetric('skipped_already_initialized', startedAt);
      return;
    }

    await this.initializeOpfsDatabaseOnce();
  }

  private async initializeOpfsDatabaseOnce(): Promise<void> {
    if (!this.opfsInitializationPromise) {
      this.opfsInitializationPromise = this.initializeOpfsDatabase();
    }

    await this.opfsInitializationPromise;
  }

  private async initializeStoragePersistenceOnce(): Promise<void> {
    // In Capacitor (native) mode, storage is managed by the app's native sandbox.
    // navigator.storage.persist() is always denied in Android/iOS WebView and the
    // PWA installation listeners are irrelevant — skip entirely.
    if (this.isNativePlatform) {
      return Promise.resolve();
    }

    if (!this.storageInitializationPromise) {
      this.storageInitializationPromise = this.initializeStoragePersistenceWithFallback();
    }

    await this.storageInitializationPromise;
  }

  private async initializeStoragePersistenceWithFallback(): Promise<void> {
    try {
      await initializeStoragePersistence();
    } catch (error) {
      console.warn('[StartupMetrics] storage_persistence_init outcome=fallback', error);
    }
  }

  private async initializeOpfsDatabase(): Promise<void> {
    const startedAt = getNowMs();
    let requiresExistingDatabase = false;

    try {
      if (this.databaseService.isInitialized()) {
        this.logStartupMetric('skipped_already_initialized', startedAt);
        return;
      }

      requiresExistingDatabase = await this.shouldRequireExistingOpfsDatabase();
      if (!requiresExistingDatabase) {
        // No user profile → first-run / registration flow.
        // Skip early OPFS initialization; the registration wizard will
        // initialize the database during its final step.
        this.logStartupMetric('skipped_registration', startedAt);
        return;
      }

      logOpfsDatabaseDiagnostics('early_init_preflight', await collectOpfsDatabaseDiagnostics());
      await assertExistingOpfsDatabaseFile();

      await this.databaseService.initialize({
        runMigrations: true,
      });
      this.logStartupMetric('success', startedAt);
    } catch (error) {
      if (requiresExistingDatabase) {
        logOpfsDatabaseDiagnostics('early_init_fallback', await collectOpfsDatabaseDiagnostics(), 'warn');
      }
      this.logStartupMetric('fallback', startedAt, error);
    } finally {
      this.opfsInitializationPromise = null;
    }
  }

  private logStartupMetric(
    outcome: 'success' | 'fallback' | 'skipped_non_opfs' | 'skipped_already_initialized' | 'skipped_registration',
    startedAt: number,
    error?: unknown,
  ): void {
    const durationMs = Math.round((getNowMs() - startedAt) * 100) / 100;
    const message = `[StartupMetrics] opfs_early_init outcome=${outcome} duration_ms=${durationMs}`;

    if (outcome === 'fallback') {
      console.warn(`${message} (auth-time fallback enabled)`, error);
      return;
    }

    console.info(message);
  }

  private async shouldRequireExistingOpfsDatabase(): Promise<boolean> {
    return (await this.metadataRepository.get(STORAGE_KEYS.USER_PROFILE)) !== undefined;
  }
}
